import { Box, Card, Grid } from '@mui/material';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import MediumTypography from '../../components/formlib/MediumTypography';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import Textinput from '../../components/formlib/Textinput';
import { ReactComponent as DeleteAlertIcon } from '../../assets/images/deleteAlertIcon.svg';
import { Formik, FormikProps } from 'formik';
import dayjs from 'dayjs';
import * as yup from 'yup';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);
import ButtonComponent from '../../components/formlib/ButtonComponent';
import {
  datePickerMinDateMaxDateValidate,
  getFinancialYear,
  getFinancialYearJulyFirstDate,
  getFinancialYearJuneEndDate,
  getFiscalYearInfo,
} from '../../utils/dateUtil';
import {
  Holiday,
  HolidayRequest,
  createHolidayList,
  deleteHoliday,
  getHolidayList,
  updateHolidayList,
} from '../../services/configApi/employees/holidayServices';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { ResponseType } from '../../utils/type';
import ModalPopup from '../../components/formlib/ModalPopup';
import { isEqual } from 'lodash';
import { useIntl } from 'react-intl';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { useNavigate } from 'react-router';
import { PROGRAM_MANAGEMENT_ROUTE_NAME } from '../../routes/Routing';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import SelectComponent from '../../components/formlib/SelectComponent';

const validationSchema = yup.array().of(
  yup.object().shape({
    holidayDate: yup
      .string()
      .required('dateRequiredErrorMessage')
      .test('is-unique', 'duplicateDateMessage', function (value, context) {
        const values = context.options.context?.map(
          (obj: { holidayDate: string; title: string }) => obj.holidayDate,
        ); // get all values to check
        return values.filter((val: string) => val === value).length <= 1; // check if value exists only once
      })
      .nullable(),
    title: yup.string().required('titleRequiredErrorMessage'),
  }),
);

interface HolidayProp {
  programId: string;
}

const CreateHolidayList: FC<HolidayProp> = ({ programId }) => {
  const initialValues: Holiday[] = [
    {
      id: '',
      holidayDate: null,
      title: '',
    },
    {
      id: '',
      holidayDate: null,
      title: '',
    },
    {
      id: '',
      holidayDate: null,
      title: '',
    },
    {
      id: '',
      holidayDate: null,
      title: '',
    },
    {
      id: '',
      holidayDate: null,
      title: '',
    },
  ];

  const rctl = useIntl();
  const [numOfDays, setNumOfDays] = useState<number>(5);
  const formikRef = useRef<FormikProps<Holiday[]>>(null);
  const [holidays, setHolidays] = useState<Holiday[]>(
    JSON.parse(JSON.stringify(initialValues)),
  );
  const [originalHolidays, setOriginalHolidays] = useState<Holiday[]>(
    JSON.parse(JSON.stringify(initialValues)),
  );
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [toastrId, setToastrId] = useState<string>();
  const [defaultMessage, setDefaultMessage] = useState<string>();
  const [showCancelModel, setShowCancelModel] = useState<boolean>(false);
  const navigate = useNavigate();
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const { fiscalYears: yearDropdownValues } = getFiscalYearInfo(new Date());
  const [selectedYear, setSelectedYear] = useState<string>(
    yearDropdownValues[2],
  );

  const validateForm = (value: Holiday[]): Partial<Holiday>[] => {
    const errors: Partial<Holiday>[] = [];

    // Helper function to ensure error object exists
    const ensureErrorObject = (index: number) => {
      if (!errors[index]) errors[index] = {};
    };

    // Helper function to validate date range
    const validateDateRange = (holiday: Holiday, index: number) => {
      const julyFirst = getFinancialYearJulyFirstDate(selectedYear);
      const juneEnd = getFinancialYearJuneEndDate(selectedYear);

      if (
        !dayjs(holiday.holidayDate).isBetween(julyFirst, juneEnd, null, '[]')
      ) {
        ensureErrorObject(index);
        errors[index].holidayDate = 'dateRangeErrorMessage';
      }
    };

    // Helper function to check duplicate dates
    const validateDuplicateDates = (
      holiday: Holiday,
      index: number,
      allHolidays: Holiday[],
    ) => {
      const holidayDates = allHolidays.map((obj) => obj.holidayDate);
      if (
        holidayDates.filter((val) => val === holiday.holidayDate).length > 1
      ) {
        ensureErrorObject(index);
        errors[index].holidayDate = 'duplicateDateMessage';
      }
    };

    // Helper function to validate basic holiday fields
    const validateBasicFields = (holiday: Holiday, index: number) => {
      if (datePickerMinDateMaxDateValidate(holiday.holidayDate)) {
        ensureErrorObject(index);
        errors[index].holidayDate = 'datePickerMinDateMaxDateValidate';
      }

      if (holiday.holidayDate === null) {
        ensureErrorObject(index);
        errors[index].holidayDate = 'dateRequiredErrorMessage';
      }

      if (holiday.title === '') {
        ensureErrorObject(index);
        errors[index].title = 'titleRequiredErrorMessage';
      }
    };

    value.forEach((holiday, index) => {
      validateBasicFields(holiday, index);

      if (holiday.holidayDate) {
        validateDateRange(holiday, index);
        validateDuplicateDates(holiday, index, value);
      }
    });

    return errors;
  };

  useEffect(() => {
    toggleLoader(true);
    const [startYear, endYear] = selectedYear.split('-').map(Number);
    getHolidayList(startYear, endYear, programId)
      .then((holidayResponse) => {
        if (holidayResponse.holidayList.length > 0) {
          setNumOfDays(holidayResponse.holidayList.length);
          setHolidays(holidayResponse.holidayList);
          setOriginalHolidays(
            JSON.parse(JSON.stringify(holidayResponse.holidayList)),
          );
        } else {
          setNumOfDays(5);
          formikRef.current?.setValues(
            JSON.parse(JSON.stringify(initialValues)),
            true,
          );
          setHolidays(JSON.parse(JSON.stringify(initialValues)));
          setOriginalHolidays(JSON.parse(JSON.stringify(initialValues)));
        }
        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setDefaultMessage(apiError.message);
        } else {
          setToastrId('holidayFetchFailureMessage');
          setDefaultMessage('Failed to fetch Holidays');
        }
      });
  }, [selectedYear]);

  useEffect(() => {
    if (!isEqual(holidays, originalHolidays)) {
      handleUpdateUnsavedChanges(true);
    } else {
      handleUpdateUnsavedChanges(false);
    }
  }, [holidays, originalHolidays]);

  const deleteExistingHoliday = (i: number, id: string) => {
    toggleLoader(true);
    deleteHoliday(id, programId)
      .then(() => {
        const filteredHolidays = holidays.filter((_, index) => index !== i);
        formikRef.current?.setValues(filteredHolidays);
        setHolidays(filteredHolidays);
        setOriginalHolidays(JSON.parse(JSON.stringify(filteredHolidays)));
        setNumOfDays(numOfDays - 1);
        setSuccessOrError('success');
        setToastrId('holidayDeleteSuccessMessage');
        setDefaultMessage('Holiday deleted successfully');
        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setDefaultMessage(apiError.message);
        } else {
          setToastrId('holidayDeleteFailureMessage');
          setDefaultMessage('Failed to delete Holiday');
        }
      });
  };

  const handleFormSubmission = async () => {
    const holidayCreateRequest: HolidayRequest = {
      holidayList: [],
      programId: programId,
    };
    const holidayUpdateRequest: HolidayRequest = {
      holidayList: [],
      programId: programId,
    };

    holidays.forEach((holiday) => {
      if (holiday.id === '') {
        holidayCreateRequest.holidayList.push(holiday);
      } else {
        holidayUpdateRequest.holidayList.push(holiday);
      }
    });
    if (
      holidayCreateRequest.holidayList.length === 0 &&
      holidayUpdateRequest.holidayList.length === 0
    ) {
      return;
    }

    let holidaysCreated = false;
    let holidaysUpdated = false;

    toggleLoader(true);
    if (holidayCreateRequest.holidayList.length > 0) {
      const response = await createHolidayList(holidayCreateRequest, programId);
      if (response.ok) {
        holidays.forEach((holiday) => {
          if (response.data) {
            response.data.holidayList.forEach((item) => {
              if (item.holidayDate === holiday.holidayDate) {
                holiday.id = item.id;
              }
            });
          }
        });
        holidaysCreated = true;
      }
    } else {
      holidaysCreated = true;
    }
    if (holidayUpdateRequest.holidayList.length > 0) {
      const response = await updateHolidayList(holidayUpdateRequest);
      if (response.ok) {
        setOriginalHolidays(JSON.parse(JSON.stringify(holidays)));
        holidaysUpdated = true;
      }
    } else {
      holidaysUpdated = true;
    }

    const updatedHolidays = [...holidays];
    setHolidays(updatedHolidays);
    setOriginalHolidays(JSON.parse(JSON.stringify(updatedHolidays)));
    toggleLoader(false);
    if (holidaysCreated && holidaysUpdated) {
      setSuccessOrError('success');
      setToastrId('holidaysSaveSuccessMessage');
    } else {
      setSuccessOrError('error');
      setToastrId('holidaySaveFailureMessage');
    }
  };

  const handleDelete = (i: number) => {
    const holiday = holidays[i];
    if (holiday.id === '') {
      const filteredHolidays = holidays.filter((__, index) => index !== i);
      formikRef.current?.setValues(filteredHolidays);
      setHolidays(filteredHolidays);
      setNumOfDays(numOfDays - 1);
    } else {
      const id = holiday.id;
      if (id) {
        deleteExistingHoliday(i, id);
      }
    }
  };

  return (
    <Box component="div">
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={defaultMessage}
          successOrError={successOrError}
        />
      )}
      {
        <ModalPopup
          description="formUnsavedChangesMessage"
          open={showCancelModel}
          onCancel={() => {
            setShowCancelModel(false);
          }}
          onOk={() => {
            setShowCancelModel(false);
            setSuccessOrError('success');
            setToastrId('formClearSuccessMessage');
            if (originalHolidays.length > 0) {
              setNumOfDays(originalHolidays.length);
              setHolidays(JSON.parse(JSON.stringify(originalHolidays)));
            }
          }}
          maxWidth={'xs'}
          labelId1="Clientpage.Nobtn"
          negativeActionLabel="No"
          labelId2="Clientpage.Yesbtn"
          positiveActionLabel="Yes"
        />
      }
      <Box component="div">
        <Formik
          innerRef={formikRef}
          initialValues={holidays}
          validationSchema={validationSchema}
          validateOnMount
          validate={validateForm}
          enableReinitialize
          onSubmit={() => {
            handleFormSubmission();
          }}
        >
          {({ errors, touched, setFieldValue }) => (
            <Box component="div">
              <Card className="formCardview">
                <Box className="flex__ mb-sm">
                  <MediumTypography
                    labelid={`${rctl.formatMessage({
                      id: 'holidaysText',
                      defaultMessage: 'Holidays',
                    })} :- `}
                    defaultlabel="Holidays"
                    className="mainText-xlg"
                  />
                  <MediumTypography
                    labelid={getFinancialYear(selectedYear)}
                    sxProps={{
                      color: '#00C6B8',
                      flexGrow: 1,
                    }}
                    className="mainText-xlg pl-xs"
                  />
                  <Box>
                    <SelectComponent
                      names={yearDropdownValues}
                      value={selectedYear}
                      handleChange={(value) => setSelectedYear(value)}
                    />
                  </Box>
                </Box>
                <Box component="div" className="p-lg">
                  <Grid container rowSpacing={3}>
                    {[...Array(numOfDays)].map((_, i) => (
                      <Grid
                        container
                        key={(i + 1).toString()}
                        item
                        xs={12}
                        display={'flex'}
                        alignItems={'center'}
                        columnSpacing={3}
                      >
                        <Grid container rowSpacing={3} columnSpacing={3}>
                          <Grid item xs={0.3}>
                            <MediumTypography
                              label={'0' + (i + 1)}
                              sxProps={{ color: '#00C6B8', paddingTop: '8px' }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <DatePickerComponent
                              labelid="dateTextHoliday"
                              required={true}
                              defaultlabelid="Date *"
                              value={
                                holidays[i].holidayDate !== null
                                  ? dayjs(holidays[i].holidayDate)
                                  : null
                              }
                              minDate={getFinancialYearJulyFirstDate(
                                selectedYear,
                              )}
                              maxDate={getFinancialYearJuneEndDate(
                                selectedYear,
                              )}
                              handlechange={(date) => {
                                const formattedDate =
                                  date === null
                                    ? null
                                    : dayjs(date).format('MM/DD/YYYY');

                                // Update the holidays array directly
                                holidays[i].holidayDate = formattedDate;

                                // Update form field value
                                setFieldValue(
                                  'holidays[i].date',
                                  formattedDate,
                                  true,
                                );

                                // Update state with direct assignment
                                const updatedHolidays = [...holidays];
                                updatedHolidays[i].holidayDate = formattedDate;
                                setHolidays(updatedHolidays);
                              }}
                            />
                            {errors?.[i]?.holidayDate &&
                              touched?.[i]?.holidayDate && (
                                <MediumTypography
                                  className="errorText-md"
                                  labelid={errors[i]?.holidayDate}
                                  defaultlabel="Field should not be empty"
                                />
                              )}
                          </Grid>
                          <Grid item xs={4}>
                            <Textinput
                              Value={holidays[i].title}
                              labelid="titleText"
                              Required={true}
                              defaultlabelid="Title"
                              inputProps={{
                                maxLength: 75,
                              }}
                              handlechange={(value) => {
                                holidays[i].title = value;
                                setFieldValue('holidays[i].title', value, true);
                                const updatedHolidays = [...holidays];
                                updatedHolidays[i].title = value;
                                setHolidays(updatedHolidays);
                              }}
                            />
                            {errors?.[i]?.title && touched?.[i]?.title && (
                              <MediumTypography
                                className="errorText-md"
                                labelid={errors[i]?.title}
                                defaultlabel="Field should not be empty"
                              />
                            )}
                          </Grid>
                          {holidays.length > 1 && (
                            <Grid item sx={{ cursor: 'pointer' }}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  paddingTop: '6px',
                                }}
                                onClick={() => handleDelete(i)}
                              >
                                <DeleteAlertIcon />
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                <Box component="div" sx={{ display: 'inline-flex' }}>
                  <MediumTypography
                    labelid="addHolidayText"
                    defaultlabel="+ Add Holiday"
                    onClick={() => {
                      const newHolidays = [...holidays];
                      newHolidays.push({
                        id: '',
                        holidayDate: null,
                        title: '',
                      });
                      formikRef.current?.setValues(newHolidays, true);
                      setHolidays(newHolidays);
                      setNumOfDays(numOfDays + 1);
                    }}
                    sxProps={{
                      color: '#00C6B8',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      marginLeft: '36px',
                    }}
                  />
                </Box>
              </Card>

              <Box component="div">
                <Grid
                  className="flex__ justifyContent-FlexEnd pt-md pb-lg "
                  container
                  direction="row"
                  alignItems="right"
                >
                  <Grid item>
                    <ButtonComponent
                      className="btn-primary btn-cancel btn_width_90px mr-md"
                      labelId="Contacts.cancelbtn"
                      defaultLabelId="Cancel"
                      variantType={'outlined'}
                      onClick={() => {
                        if (!isEqual(holidays, originalHolidays)) {
                          setShowCancelModel(!showCancelModel);
                        } else {
                          navigate(PROGRAM_MANAGEMENT_ROUTE_NAME);
                        }
                      }}
                    />

                    <ButtonComponent
                      className="btn-primary btn-submit"
                      variantType={'contained'}
                      labelId="Insurance.save"
                      defaultLabelId="Save"
                      onClick={() => {
                        formikRef.current?.handleSubmit();
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default CreateHolidayList;
